import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Plus, CheckCircle, X, Calendar, Pencil, ArrowsClockwise } from '@phosphor-icons/react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const today = () => new Date().toISOString().split('T')[0];

const LeavesPage = () => {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';

  const [balances, setBalances] = useState([]);
  const [requests, setRequests] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [showManualForm, setShowManualForm] = useState(false);
  const [form, setForm] = useState({ employee_id: '', from_date: today(), to_date: today(), reason: '', leave_type: 'Sick' });
  const [manualForm, setManualForm] = useState({ employee_id: '', from_date: today(), to_date: today(), reason: 'Backfilled from records', leave_type: 'Casual', decision_note: 'Manually recorded' });
  const [saving, setSaving] = useState(false);
  // ── Earned-leave override modal state
  const [overrideRow, setOverrideRow] = useState(null);   // balance row being edited
  const [overrideEarned, setOverrideEarned] = useState('');
  const [overrideNote, setOverrideNote] = useState('');
  const [savingOverride, setSavingOverride] = useState(false);

  const openOverride = (b) => {
    setOverrideRow(b);
    setOverrideEarned(String(b.manual_override != null ? b.manual_override : b.earned_days));
    setOverrideNote(b.override_note || '');
  };
  const submitOverride = async (clear = false) => {
    if (!overrideRow) return;
    setSavingOverride(true);
    try {
      await axios.put(`${API_URL}/api/employees/${overrideRow.employee_id}/leave-balance`, {
        earned: clear ? null : parseFloat(overrideEarned),
        note: overrideNote,
      }, { withCredentials: true });
      toast.success(clear ? 'Override cleared — reverted to auto-computed' : 'Earned leave updated');
      setOverrideRow(null);
      await loadAll();
    } catch (e) {
      toast.error('Failed: ' + (e.response?.data?.detail || e.message));
    } finally { setSavingOverride(false); }
  };

  const loadAll = async () => {
    setLoading(true);
    try {
      const [bRes, rRes, eRes] = await Promise.all([
        isAdmin ? axios.get(`${API_URL}/api/leaves/balances`, { withCredentials: true }) : Promise.resolve({ data: [] }),
        axios.get(`${API_URL}/api/leaves/requests`, { withCredentials: true }),
        axios.get(`${API_URL}/api/employees`, { withCredentials: true }),
      ]);
      setBalances(bRes.data || []);
      setRequests(rRes.data || []);
      setEmployees(eRes.data || []);
    } catch (e) {
      console.error('leaves load', e);
    } finally { setLoading(false); }
  };
  useEffect(() => { loadAll(); /* eslint-disable-next-line */ }, [isAdmin]);

  const submitRequest = async () => {
    if (!form.employee_id || !form.from_date || !form.to_date) { toast.error('Employee, From & To dates are required'); return; }
    setSaving(true);
    try {
      await axios.post(`${API_URL}/api/leaves/requests`, form, { withCredentials: true });
      toast.success('Leave request submitted');
      setShowForm(false);
      setForm({ employee_id: '', from_date: today(), to_date: today(), reason: '', leave_type: 'Sick' });
      loadAll();
    } catch (e) {
      toast.error('Failed: ' + (e.response?.data?.detail || e.message));
    } finally { setSaving(false); }
  };

  const submitManual = async () => {
    if (!manualForm.employee_id || !manualForm.from_date || !manualForm.to_date) {
      toast.error('Employee, From & To dates are required'); return;
    }
    setSaving(true);
    try {
      await axios.post(`${API_URL}/api/leaves/manual`, manualForm, { withCredentials: true });
      toast.success('Leave entry recorded (auto-approved)');
      setShowManualForm(false);
      setManualForm({ employee_id: '', from_date: today(), to_date: today(), reason: 'Backfilled from records', leave_type: 'Casual', decision_note: 'Manually recorded' });
      loadAll();
    } catch (e) {
      toast.error('Failed: ' + (e.response?.data?.detail || e.message));
    } finally { setSaving(false); }
  };

  const decide = async (req, decision) => {
    if (!window.confirm(`${decision === 'approved' ? 'Approve' : 'Reject'} this leave request for ${req.employee_name}?`)) return;
    try {
      await axios.post(`${API_URL}/api/leaves/requests/${req.id}/decide`, { decision }, { withCredentials: true });
      toast.success(`Leave ${decision}`);
      loadAll();
    } catch (e) { toast.error('Failed: ' + (e.response?.data?.detail || e.message)); }
  };

  const removeReq = async (req) => {
    if (!window.confirm('Cancel this leave request?')) return;
    try {
      await axios.delete(`${API_URL}/api/leaves/requests/${req.id}`, { withCredentials: true });
      toast.success('Leave cancelled');
      loadAll();
    } catch (e) { toast.error('Failed: ' + (e.response?.data?.detail || e.message)); }
  };

  const pending = useMemo(() => requests.filter((r) => r.status === 'pending'), [requests]);
  const decided = useMemo(() => requests.filter((r) => r.status !== 'pending'), [requests]);

  const dateRange = (from, to) => {
    if (from === to) return from;
    return `${from} → ${to}`;
  };
  const fmtDays = (d) => `${Number(d).toFixed(d % 1 ? 1 : 0)} day${d === 1 ? '' : 's'}`;

  // Color/emoji per leave type, with a sensible default for legacy entries
  const TYPE_LABELS = {
    Sick: { l: 'Sick Leave', bg: '#fee2e2', fg: '#991b1b', icon: '🤒' },
    Casual: { l: 'Casual Leave', bg: '#dbeafe', fg: '#1e40af', icon: '🌴' },
    Paid: { l: 'Paid Leave', bg: '#dcfce7', fg: '#166534', icon: '🏖️' },
    Unpaid: { l: 'Unpaid Leave', bg: '#f3f4f6', fg: '#374151', icon: '⊘' },
    'Maternity/Paternity': { l: 'Maternity/Paternity', bg: '#fce7f3', fg: '#9d174d', icon: '👶' },
    Bereavement: { l: 'Bereavement', bg: '#e5e7eb', fg: '#1f2937', icon: '🕊️' },
    // legacy fallbacks (kept so old records still render)
    Earned: { l: 'Earned', bg: '#dcfce7', fg: '#166534', icon: '🏖️' },
    'Comp Off': { l: 'Comp Off', bg: '#fef3c7', fg: '#92400e', icon: '🔄' },
    Maternity: { l: 'Maternity', bg: '#fce7f3', fg: '#9d174d', icon: '👶' },
    Paternity: { l: 'Paternity', bg: '#e0e7ff', fg: '#3730a3', icon: '👨' },
  };
  // Types shown in the dropdown (excludes legacy aliases)
  const ACTIVE_TYPES = ['Sick', 'Casual', 'Paid', 'Unpaid', 'Maternity/Paternity', 'Bereavement'];
  const TypeChip = ({ t }) => {
    const c = TYPE_LABELS[t] || { l: t || '—', bg: '#f3f4f6', fg: '#374151', icon: '•' };
    return (
      <span className="inline-flex items-center text-[10px] font-semibold px-2 py-0.5 rounded-full"
        style={{ background: c.bg, color: c.fg }}>
        <span className="mr-1">{c.icon}</span>{c.l}
      </span>
    );
  };

  return (
    <div data-testid="leaves-page" className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold tracking-tight" style={{ color: 'var(--dx-text)' }}>
            <Calendar size={22} className="inline mr-1.5 mb-1" /> Leave Management
          </h1>
          <p className="text-sm mt-1" style={{ color: 'var(--dx-text2)' }}>
            Probation: 4 months. Post-probation, employees earn <strong>1 paid leave per completed month</strong>. Carry-forward is unlimited.
          </p>
        </div>
        <div className="flex gap-2">
          {isAdmin && (
            <Button variant="outline" onClick={() => setShowManualForm(true)} data-testid="manual-leave-btn">
              <Calendar size={14} className="mr-1.5" /> Manual Leave Entry
            </Button>
          )}
          <Button onClick={() => setShowForm(true)} data-testid="new-leave-btn">
            <Plus size={14} className="mr-1.5" /> Request Leave
          </Button>
        </div>
      </div>

      {/* Pending Approvals (admin only) */}
      {isAdmin && pending.length > 0 && (
        <Card className="p-0 overflow-hidden border-l-4" style={{ borderLeftColor: '#f59e0b' }}>
          <div className="px-4 py-2.5 text-sm font-semibold" style={{ background: '#fffbeb', color: '#92400e' }}>
            🟡 {pending.length} pending approval{pending.length !== 1 ? 's' : ''}
          </div>
          <table className="w-full text-sm">
            <thead style={{ background: 'var(--dx-bg)' }}>
              <tr>
                <th className="text-left px-3 py-2 font-semibold text-xs uppercase">Employee</th>
                <th className="text-left px-3 py-2 font-semibold text-xs uppercase">Type</th>
                <th className="text-left px-3 py-2 font-semibold text-xs uppercase">Period</th>
                <th className="text-left px-3 py-2 font-semibold text-xs uppercase">Days</th>
                <th className="text-left px-3 py-2 font-semibold text-xs uppercase">Reason</th>
                <th className="text-right px-3 py-2 font-semibold text-xs uppercase">Action</th>
              </tr>
            </thead>
            <tbody>
              {pending.map((r) => (
                <tr key={r.id} className="border-t" data-testid={`pending-${r.id}`}>
                  <td className="px-3 py-2"><strong>{r.employee_name}</strong> <span className="text-[10px] text-gray-500">{r.emp_label}</span></td>
                  <td className="px-3 py-2"><TypeChip t={r.leave_type} /></td>
                  <td className="px-3 py-2 text-[12px]">{dateRange(r.from_date, r.to_date)}</td>
                  <td className="px-3 py-2 text-[12px] font-semibold">{fmtDays(r.days)}</td>
                  <td className="px-3 py-2 text-[12px] text-gray-600">{r.reason || '—'}</td>
                  <td className="px-3 py-2 text-right">
                    <div className="flex justify-end gap-1.5">
                      <Button size="sm" variant="outline" className="text-green-700 border-green-300" onClick={() => decide(r, 'approved')} data-testid={`approve-${r.id}`}>
                        <CheckCircle size={14} className="mr-1" weight="fill" /> Approve
                      </Button>
                      <Button size="sm" variant="outline" className="text-red-600 border-red-300" onClick={() => decide(r, 'rejected')} data-testid={`reject-${r.id}`}>
                        <X size={14} className="mr-1" /> Reject
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}

      {/* Balances (admin only) */}
      {isAdmin && (
        <Card className="p-0 overflow-x-auto">
          <div className="px-4 py-2.5 text-sm font-semibold" style={{ background: 'var(--dx-bg)' }}>
            Employee Leave Balances ({balances.length})
          </div>
          <table className="w-full text-sm">
            <thead style={{ background: 'var(--dx-bg)' }}>
              <tr>
                <th className="text-left px-3 py-2 font-semibold text-xs uppercase">Emp ID</th>
                <th className="text-left px-3 py-2 font-semibold text-xs uppercase">Name</th>
                <th className="text-left px-3 py-2 font-semibold text-xs uppercase">Joined</th>
                <th className="text-center px-3 py-2 font-semibold text-xs uppercase">Months</th>
                <th className="text-center px-3 py-2 font-semibold text-xs uppercase">Eligible</th>
                <th className="text-center px-3 py-2 font-semibold text-xs uppercase">Earned</th>
                <th className="text-center px-3 py-2 font-semibold text-xs uppercase">Used</th>
                <th className="text-center px-3 py-2 font-semibold text-xs uppercase" style={{ background: '#e0f2fe' }}>Balance</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={8} className="text-center py-8 text-[#52525B]">Loading…</td></tr>
              ) : balances.length === 0 ? (
                <tr><td colSpan={8} className="text-center py-8 text-[#52525B]">No active employees.</td></tr>
              ) : balances.map((b) => (
                <tr key={b.employee_id} className="border-t hover:bg-gray-50" data-testid={`balance-${b.emp_id}`}>
                  <td className="px-3 py-2 font-mono text-[11px]">{b.emp_id}</td>
                  <td className="px-3 py-2 font-medium">{b.name}</td>
                  <td className="px-3 py-2 text-[12px] text-gray-600">{b.joining_date || '—'}</td>
                  <td className="px-3 py-2 text-center text-[12px]">{b.months_since_joining}</td>
                  <td className="px-3 py-2 text-center text-[11px]">
                    {b.eligible ? <span style={{ color: '#16a34a' }}>✓ yes</span> : <span style={{ color: '#9ca3af' }}>probation</span>}
                  </td>
                  <td className="px-3 py-2 text-center text-[12px] font-semibold">
                    <div className="inline-flex items-center gap-1.5">
                      <span data-testid={`balance-earned-${b.emp_id}`}>{b.earned_days}</span>
                      {b.manual_override != null && (
                        <span className="text-[9px] font-bold px-1 py-0.5 rounded bg-amber-100 text-amber-700" title={`Manual override (auto-computed was ${b.computed_earned})`}>
                          OVR
                        </span>
                      )}
                      <button
                        type="button"
                        onClick={() => openOverride(b)}
                        className="p-0.5 rounded hover:bg-emerald-50 text-emerald-700"
                        title="Edit earned leave (admin override)"
                        data-testid={`edit-earned-${b.emp_id}`}
                      ><Pencil size={12} /></button>
                    </div>
                  </td>
                  <td className="px-3 py-2 text-center text-[12px]" style={{ color: '#dc2626' }}>{b.used_days}</td>
                  <td className="px-3 py-2 text-center font-bold" style={{ background: '#e0f2fe', color: '#0c4a6e' }}>{b.balance}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}

      {/* Decided history (collapsed for everyone) */}
      <Card className="p-0">
        <div className="px-4 py-2.5 text-sm font-semibold" style={{ background: 'var(--dx-bg)' }}>
          Recent Leave History ({decided.length})
        </div>
        <table className="w-full text-sm">
          <thead style={{ background: 'var(--dx-bg)' }}>
            <tr>
              <th className="text-left px-3 py-2 font-semibold text-xs uppercase">Employee</th>
              <th className="text-left px-3 py-2 font-semibold text-xs uppercase">Type</th>
              <th className="text-left px-3 py-2 font-semibold text-xs uppercase">Period</th>
              <th className="text-left px-3 py-2 font-semibold text-xs uppercase">Days</th>
              <th className="text-left px-3 py-2 font-semibold text-xs uppercase">Status</th>
              <th className="text-left px-3 py-2 font-semibold text-xs uppercase">Decided By</th>
            </tr>
          </thead>
          <tbody>
            {decided.length === 0 ? (
              <tr><td colSpan={6} className="text-center py-8 text-[#52525B]">No history yet.</td></tr>
            ) : decided.slice(0, 50).map((r) => (
              <tr key={r.id} className="border-t" data-testid={`history-${r.id}`}>
                <td className="px-3 py-2">{r.employee_name} <span className="text-[10px] text-gray-500">{r.emp_label}</span></td>
                <td className="px-3 py-2"><TypeChip t={r.leave_type} /></td>
                <td className="px-3 py-2 text-[12px]">{dateRange(r.from_date, r.to_date)}</td>
                <td className="px-3 py-2 text-[12px]">{fmtDays(r.days)}</td>
                <td className="px-3 py-2">
                  <span className="text-[10px] font-bold px-2 py-0.5 rounded-full" style={{
                    background: r.status === 'approved' ? '#dcfce7' : '#fee2e2',
                    color: r.status === 'approved' ? '#15803d' : '#b91c1c',
                  }}>
                    {r.status === 'approved' ? '✓ APPROVED' : '✗ REJECTED'}
                  </span>
                </td>
                <td className="px-3 py-2 text-[11px] text-gray-600">{r.decided_by || '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>

      {/* Form modal */}
      {showForm && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={() => setShowForm(false)}>
          <Card className="w-full max-w-lg bg-white p-5" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-bold">Request Leave</h2>
              <button onClick={() => setShowForm(false)} className="text-2xl leading-none px-2">×</button>
            </div>
            <div className="space-y-3">
              <div className="space-y-1">
                <Label className="text-xs">Employee *</Label>
                <select value={form.employee_id} onChange={(e) => setForm({ ...form, employee_id: e.target.value })}
                  className="w-full border rounded px-3 py-2 text-sm bg-white" data-testid="leave-employee-select">
                  <option value="">— select —</option>
                  {employees.filter((e) => e.is_active !== false).map((e) => (
                    <option key={e.id} value={e.id}>{e.name} ({e.emp_id})</option>
                  ))}
                </select>
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Leave Type *</Label>
                <select value={form.leave_type} onChange={(e) => setForm({ ...form, leave_type: e.target.value })}
                  className="w-full border rounded px-3 py-2 text-sm bg-white" data-testid="leave-type-select">
                  {ACTIVE_TYPES.map((t) => (
                    <option key={t} value={t}>{TYPE_LABELS[t].icon} {TYPE_LABELS[t].l}</option>
                  ))}
                </select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label className="text-xs">From *</Label>
                  <Input type="date" value={form.from_date} onChange={(e) => setForm({ ...form, from_date: e.target.value })} data-testid="leave-from-input" />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">To *</Label>
                  <Input type="date" value={form.to_date} onChange={(e) => setForm({ ...form, to_date: e.target.value })} data-testid="leave-to-input" />
                </div>
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Reason</Label>
                <textarea value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })}
                  className="w-full border rounded px-3 py-2 text-sm min-h-[60px]" placeholder="Personal / Family / Medical / Other"
                  data-testid="leave-reason-input" />
              </div>
              <p className="text-[11px] text-gray-500 pt-1">
                Note: Leave requires admin approval. Once approved, it deducts from the employee's earned balance.
              </p>
            </div>
            <div className="flex justify-end gap-2 mt-5">
              <Button variant="outline" onClick={() => setShowForm(false)} data-testid="leave-cancel-btn">Cancel</Button>
              <Button onClick={submitRequest} disabled={saving} data-testid="leave-submit-btn">
                {saving ? 'Submitting…' : 'Submit Request'}
              </Button>
            </div>
          </Card>
        </div>
      )}
      {/* Manual Leave Entry modal (admin) */}
      {showManualForm && isAdmin && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={() => setShowManualForm(false)}>
          <Card className="w-full max-w-lg bg-white p-5" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <div>
                <h2 className="text-lg font-bold">Manual Leave Entry</h2>
                <p className="text-[11px] text-gray-500 mt-0.5">Record an already-approved historical leave. Use this when backfilling data from Excel.</p>
              </div>
              <button onClick={() => setShowManualForm(false)} className="text-2xl leading-none px-2">×</button>
            </div>
            <div className="space-y-3">
              <div className="space-y-1">
                <Label className="text-xs">Employee *</Label>
                <select value={manualForm.employee_id} onChange={(e) => setManualForm({ ...manualForm, employee_id: e.target.value })}
                  className="w-full border rounded px-3 py-2 text-sm bg-white" data-testid="manual-employee-select">
                  <option value="">— select —</option>
                  {employees.filter((e) => e.is_active !== false).map((e) => (
                    <option key={e.id} value={e.id}>{e.name} ({e.emp_id})</option>
                  ))}
                </select>
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Leave Type *</Label>
                <select value={manualForm.leave_type} onChange={(e) => setManualForm({ ...manualForm, leave_type: e.target.value })}
                  className="w-full border rounded px-3 py-2 text-sm bg-white" data-testid="manual-type-select">
                  {ACTIVE_TYPES.map((t) => (
                    <option key={t} value={t}>{TYPE_LABELS[t].icon} {TYPE_LABELS[t].l}</option>
                  ))}
                </select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label className="text-xs">From *</Label>
                  <Input type="date" value={manualForm.from_date} onChange={(e) => setManualForm({ ...manualForm, from_date: e.target.value })} data-testid="manual-from-input" />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">To *</Label>
                  <Input type="date" value={manualForm.to_date} onChange={(e) => setManualForm({ ...manualForm, to_date: e.target.value })} data-testid="manual-to-input" />
                </div>
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Reason</Label>
                <Input value={manualForm.reason} onChange={(e) => setManualForm({ ...manualForm, reason: e.target.value })}
                  placeholder="e.g. Diwali holiday, Personal" data-testid="manual-reason-input" />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Decision Note (internal)</Label>
                <Input value={manualForm.decision_note} onChange={(e) => setManualForm({ ...manualForm, decision_note: e.target.value })}
                  placeholder="Recorded from Excel" data-testid="manual-note-input" />
              </div>
              <div className="bg-amber-50 border border-amber-200 rounded px-3 py-2 text-[11px] text-amber-800">
                ⚠️ This leave will be recorded as <strong>Approved</strong> immediately and will deduct from the employee's balance. No request/approval cycle.
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-5">
              <Button variant="outline" onClick={() => setShowManualForm(false)}>Cancel</Button>
              <Button onClick={submitManual} disabled={saving} data-testid="manual-submit-btn">
                {saving ? 'Saving…' : 'Save Leave Entry'}
              </Button>
            </div>
          </Card>
        </div>
      )}

      {/* Earned-leave override modal */}
      {overrideRow && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={() => !savingOverride && setOverrideRow(null)}>
          <Card className="w-full max-w-md p-5 bg-white" onClick={(e) => e.stopPropagation()} data-testid="earned-override-modal">
            <div className="flex items-start justify-between mb-3">
              <div>
                <div className="text-lg font-bold">Edit Earned Leave</div>
                <div className="text-xs text-gray-500 mt-0.5">{overrideRow.name} · {overrideRow.emp_id}</div>
              </div>
              <button onClick={() => setOverrideRow(null)} className="text-gray-400 hover:text-gray-600"><X size={20} /></button>
            </div>
            <div className="text-[11px] bg-slate-50 border rounded p-2 mb-3 space-y-0.5">
              <div className="flex justify-between"><span className="text-gray-600">Auto-computed (joining + months)</span><span className="font-semibold tabular-nums">{overrideRow.computed_earned} days</span></div>
              <div className="flex justify-between"><span className="text-gray-600">Used (approved)</span><span className="font-semibold tabular-nums text-rose-700">{overrideRow.used_days} days</span></div>
              <div className="flex justify-between"><span className="text-gray-600">Current earned (after override)</span><span className="font-bold tabular-nums">{overrideRow.earned_days} days</span></div>
            </div>
            <div className="space-y-2">
              <div>
                <Label className="text-xs">Set earned to (days)</Label>
                <Input type="number" step="0.5" value={overrideEarned} onChange={(e) => setOverrideEarned(e.target.value)} data-testid="earned-override-value" />
              </div>
              <div>
                <Label className="text-xs">Audit note (why override?)</Label>
                <Input value={overrideNote} onChange={(e) => setOverrideNote(e.target.value)} placeholder="e.g. Carried forward from FY24-25, +3 days" data-testid="earned-override-note" />
              </div>
            </div>
            <div className="flex items-center justify-between mt-4 gap-2">
              <Button variant="outline" className="text-rose-600 border-rose-200" disabled={savingOverride || overrideRow.manual_override == null} onClick={() => submitOverride(true)} data-testid="earned-override-clear">
                <ArrowsClockwise size={14} className="mr-1.5" /> Clear override
              </Button>
              <div className="flex gap-2">
                <Button variant="outline" onClick={() => setOverrideRow(null)} disabled={savingOverride}>Cancel</Button>
                <Button onClick={() => submitOverride(false)} disabled={savingOverride || overrideEarned === ''} className="bg-[#1D9E75] hover:bg-[#0F6E56] text-white" data-testid="earned-override-save">
                  {savingOverride ? 'Saving…' : 'Save'}
                </Button>
              </div>
            </div>
          </Card>
        </div>
      )}
    </div>
  );
};

export default LeavesPage;
